import type { PublicProductSummary } from '@repo/api-contracts';
import type { ReactElement } from 'react';
import { Link } from 'react-router';
import { storeProductPath } from '@/shared/config';
import { formatCount, formatKrw } from '@/shared/lib';

interface ProductCardProps {
  readonly product: PublicProductSummary;
  /** 사진 왼쪽 위 작은 흰 표식(예: HOME 추천 순서 "PICK 1") — 없으면 달지 않는다. */
  readonly badge?: string;
}

/** 보조 설명 — 실제로 있는 사실(색상 수·인쇄 방식)만 잇는다. 지어낸 문구로 빈 줄을 채우지 않는다. */
const summaryLine = (product: PublicProductSummary): string =>
  [
    product.availableColorCount > 0 ? `색상 ${formatCount(product.availableColorCount)}종` : null,
    product.printMethodNames.length > 0 ? product.printMethodNames.join(' · ') : null,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');

/**
 * 상품 카드 — 각진 회색 사진 칸(선택 표식) 아래 상품명·보조 설명, 얇은 선, 시작 가격·라임 최소 수량 표식.
 * 카드 전체가 상세로 가는 단일 링크다. HOME 추천과 스토어 목록이 같은 카드를 쓴다.
 */
export const ProductCard = ({ product, badge }: ProductCardProps): ReactElement => {
  const summary = summaryLine(product);

  return (
    <Link
      to={storeProductPath(product.id)}
      className="group flex h-full flex-col focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4"
    >
      <div className="relative aspect-[4/5] w-full overflow-hidden bg-muted/70">
        {product.thumbnail !== null ? (
          <img
            src={product.thumbnail.url}
            // 카드 이름은 아래 제목이 읽어 준다 — 사진은 장식으로 둔다.
            alt=""
            loading="lazy"
            className="size-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
          />
        ) : null}
        {badge !== undefined ? (
          <span className="absolute top-4 left-4 bg-background px-2.5 py-1 text-xs font-semibold tracking-[0.04em]">
            {badge}
          </span>
        ) : null}
      </div>

      <div className="flex flex-1 flex-col pt-5">
        <h3 className="text-[17px] leading-snug font-semibold tracking-[-0.01em]">
          {product.name}
        </h3>
        {summary !== '' ? <p className="mt-1.5 text-sm text-muted-foreground">{summary}</p> : null}

        {/* 가격 줄은 카드 아래 끝에 붙인다 — 이름이 두 줄인 카드와 나란해도 같은 높이에 선다. */}
        <div className="mt-auto pt-4">
          <div className="flex items-center gap-2.5 border-t border-foreground/10 pt-4">
            <span className="text-xl font-bold tabular-nums">
              {formatKrw(product.baseUnitPrice)}부터
            </span>
            <span className="bg-brand-lime px-1.5 py-0.5 text-xs font-semibold tabular-nums">
              최소 {formatCount(product.minOrderQty)}개
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};
